/* Karaoke Studio — MIDI / UltraStar karaoke-file import.
 *
 * Two community karaoke formats carry exact note + syllable timing, so
 * importing one gives perfect synced lyrics with zero manual work AND a
 * ready melody reference for pitch scoring (even for backing-track uploads
 * that never went through vocal separation).
 *
 *   parseUltraStar(text)        → { timeline, notes, meta }
 *   parseMidi(arrayBuffer)      → { timeline, notes, meta }
 *
 *   timeline : [{ startS, endS, text, words:[{startS,text}] }]  (KaraokeLyrics)
 *   notes    : [{ startS, endS, midiPitch, freqHz }]            (KaraokeScoring)
 *
 * All parsing is pure — unit-tested directly.
 */
(function () {
    'use strict';

    function midiToHz(m) { return 440 * Math.pow(2, (m - 69) / 12); }

    function finishLine(lines, words) {
        if (!words.length) return;
        var text = words.map(function (w) { return w.text; }).join('').trim();
        if (!text) return;
        lines.push({
            startS: words[0].startS,
            endS: words[words.length - 1].endS,
            text: text,
            words: words.map(function (w) { return { startS: w.startS, text: w.text }; }),
        });
    }

    // ── UltraStar .txt ──────────────────────────────────────────────────────
    // One beat lasts 15000/BPM ms (UltraStar quadruples the file's BPM
    // internally). #GAP is the lead-in offset in ms.
    function parseUltraStar(text) {
        var rows = String(text || '').split(/\r?\n/);
        var bpm = 0, gap = 0, meta = {};
        for (var i = 0; i < rows.length; i++) {
            var h = rows[i].match(/^#([A-Z]+):(.*)$/);
            if (!h) continue;
            var key = h[1], val = h[2].trim();
            if (key === 'BPM') bpm = parseFloat(val.replace(',', '.'));
            else if (key === 'GAP') gap = parseFloat(val.replace(',', '.')) || 0;
            else meta[key.toLowerCase()] = val;
        }
        if (!(bpm > 0)) throw new Error('ultrastar-no-bpm');
        var beatMs = 15000 / bpm;
        var beatToS = function (beat) { return (gap + beat * beatMs) / 1000; };

        var lines = [], notes = [], words = [];
        for (var r = 0; r < rows.length; r++) {
            var line = rows[r];
            if (!line) continue;
            var tag = line.charAt(0);
            if (tag === ':' || tag === '*' || tag === 'F' || tag === 'R' || tag === 'G') {
                // : startBeat length pitch text   (* golden, F freestyle, …)
                var m = line.match(/^[:*FRG]\s+(-?\d+)\s+(\d+)\s+(-?\d+)\s?(.*)$/);
                if (!m) continue;
                var start = parseInt(m[1], 10);
                var len = parseInt(m[2], 10);
                var pitch = parseInt(m[3], 10);
                var syl = m[4] || '';
                var sS = beatToS(start), eS = beatToS(start + len);
                words.push({ startS: sS, endS: eS, text: syl });
                var midi = pitch + 60;     // UltraStar pitch 0 ≈ middle-ish
                notes.push({ startS: sS, endS: eS, midiPitch: midi, freqHz: midiToHz(midi) });
            } else if (tag === '-') {
                finishLine(lines, words);
                words = [];
            } else if (tag === 'E') {
                break;
            }
        }
        finishLine(lines, words);
        if (!lines.length) throw new Error('ultrastar-empty');
        return { timeline: lines, notes: notes, meta: meta };
    }

    // ── Standard MIDI File (also .kar karaoke MIDI) ─────────────────────────
    function parseMidi(arrayBuffer) {
        var dv = new DataView(arrayBuffer);
        var pos = 0;
        function u8() { return dv.getUint8(pos++); }
        function u16() { var v = dv.getUint16(pos); pos += 2; return v; }
        function u32() { var v = dv.getUint32(pos); pos += 4; return v; }
        function varint() {
            var v = 0, b;
            do { b = u8(); v = (v << 7) | (b & 0x7f); } while (b & 0x80);
            return v;
        }
        function str(n) {
            var s = '';
            for (var i = 0; i < n; i++) s += String.fromCharCode(u8());
            return s;
        }

        if (str(4) !== 'MThd') throw new Error('midi-bad-header');
        u32();                         // header length (always 6)
        u16();                         // format
        var ntracks = u16();
        var division = u16();          // ticks per quarter note
        if (division & 0x8000) throw new Error('midi-smpte-unsupported');

        // Pass 1: collect tempo changes + raw events per track.
        var tempos = [{ tick: 0, usPerQuarter: 500000 }]; // default 120 BPM
        var lyricEvents = [];          // { tick, text }
        var noteEvents = [];           // { tick, on, pitch }

        for (var t = 0; t < ntracks; t++) {
            if (str(4) !== 'MTrk') break;
            var len = u32();
            var end = pos + len;
            var tick = 0, running = 0;
            while (pos < end) {
                tick += varint();
                var status = dv.getUint8(pos);
                if (status & 0x80) { pos++; running = status; }
                else { status = running; }
                if (status === 0xff) {
                    var type = u8();
                    var mlen = varint();
                    if (type === 0x51 && mlen === 3) {
                        var us = (u8() << 16) | (u8() << 8) | u8();
                        tempos.push({ tick: tick, usPerQuarter: us });
                    } else if (type === 0x01 || type === 0x05) {
                        // text (0x01) or lyric (0x05)
                        lyricEvents.push({ tick: tick, text: str(mlen) });
                    } else {
                        pos += mlen;
                    }
                } else if (status === 0xf0 || status === 0xf7) {
                    pos += varint();
                } else {
                    var hi = status & 0xf0;
                    var d1 = u8();
                    var d2 = (hi === 0xc0 || hi === 0xd0) ? 0 : u8();
                    if (hi === 0x90 && d2 > 0) noteEvents.push({ tick: tick, on: true, pitch: d1 });
                    else if (hi === 0x80 || (hi === 0x90 && d2 === 0)) noteEvents.push({ tick: tick, on: false, pitch: d1 });
                }
            }
            pos = end;
        }
        tempos.sort(function (a, b) { return a.tick - b.tick; });

        // tick → seconds across the tempo map.
        function tickToS(tk) {
            var s = 0, prev = 0, us = 500000;
            for (var i = 0; i < tempos.length; i++) {
                if (tempos[i].tick > tk) break;
                s += ((tempos[i].tick - prev) / division) * (us / 1e6);
                prev = tempos[i].tick;
                us = tempos[i].usPerQuarter;
            }
            s += ((tk - prev) / division) * (us / 1e6);
            return s;
        }

        // Notes: pair note-on with the next note-off of the same pitch.
        noteEvents.sort(function (a, b) { return a.tick - b.tick; });
        var notes = [], openByPitch = {};
        for (var n = 0; n < noteEvents.length; n++) {
            var ev = noteEvents[n];
            if (ev.on) {
                openByPitch[ev.pitch] = ev.tick;
            } else if (openByPitch[ev.pitch] != null) {
                var st = openByPitch[ev.pitch];
                openByPitch[ev.pitch] = null;
                notes.push({
                    startS: tickToS(st), endS: tickToS(ev.tick),
                    midiPitch: ev.pitch, freqHz: midiToHz(ev.pitch),
                });
            }
        }
        notes.sort(function (a, b) { return a.startS - b.startS; });

        // Lyrics: .kar uses '\' / '/' as line breaks, and '@'-prefixed events
        // are metadata, not sung text.
        lyricEvents.sort(function (a, b) { return a.tick - b.tick; });
        var lines = [], words = [];
        for (var k = 0; k < lyricEvents.length; k++) {
            var raw = lyricEvents[k].text;
            if (!raw || raw.charAt(0) === '@') continue;
            var startS = tickToS(lyricEvents[k].tick);
            var brk = /^[\\/]/.test(raw);
            if (brk) { finishLine(lines, words); words = []; raw = raw.replace(/^[\\/]/, ''); }
            if (raw) words.push({ startS: startS, endS: startS + 0.5, text: raw.replace(/_/g, ' ') });
        }
        finishLine(lines, words);

        return { timeline: lines, notes: notes, meta: {} };
    }

    // Dispatch by file content / extension.
    function parse(nameOrExt, dataOrText) {
        var ext = String(nameOrExt || '').split('.').pop().toLowerCase();
        if (ext === 'mid' || ext === 'midi' || ext === 'kar') {
            return parseMidi(dataOrText);
        }
        return parseUltraStar(dataOrText);
    }

    window.KaraokeImport = {
        parseUltraStar: parseUltraStar,
        parseMidi: parseMidi,
        parse: parse,
        midiToHz: midiToHz,
    };
})();
